/**
 * 판정 기록 조회 훅 (D-12 — staleTime 0·DoseApiError).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * 봉투 error≠0이면 throw new Error가 아니라 DoseApiError로 던진다(코드·data 보존 — 6120·6125 분기).
 * 목록·요약·상세·neighbors·당시 룰 전부 staleTime 0(전역 5분을 끈다, D-12). 쿼리 키는 상수로 export해
 * 직접 진입 기간 고정(결정 C1)이 setQueryData로 시드할 수 있게 한다.
 */
import { useQuery, useQueries } from "react-query";
import { DoseApiError } from "services/api/doseApiError";
import { doseReviewLogsApi } from "services/api/doseReviewLogs/doseReviewLogsApi";
import { doseRuleAdminApi } from "services/api/doseRules/doseRuleAdminApi";
import type {
  DoseReviewLogEnvelope,
  DoseReviewLogFilter,
  DoseReviewLogListQuery,
  DoseReviewLogListData,
  DoseReviewLogSummaryData,
  DoseReviewLogNeighbors,
  DoseReviewLogDetailData,
} from "services/api/doseReviewLogs/types";
import type { AdminDoseRuleVersionDetail } from "services/api/doseRules/types";

/** 봉투 → data, 실패면 DoseApiError(코드·data 보존). */
const unwrap = <T>(res: DoseReviewLogEnvelope<T>): T => {
  if (res.error !== 0 || res.data == null) {
    throw new DoseApiError({ code: res.error, message: res.message, data: res.data });
  }
  return res.data;
};

type ApiRange = { from: string; to: string };

/** 쿼리 키 — 결정 C1 시드(직접 진입 기간 고정)·무효화(Phase 4)가 참조한다. */
export const doseReviewLogKeys = {
  list: (query: DoseReviewLogListQuery) => ["doseReviewLogs", "list", query] as const,
  summary: (filter: DoseReviewLogFilter) => ["doseReviewLogs", "summary", filter] as const,
  detail: (logId: number, range?: ApiRange) =>
    ["doseReviewLogs", "detail", logId, range ?? null] as const,
  neighbors: (logId: number, filter: DoseReviewLogFilter) =>
    ["doseReviewLogs", "neighbors", logId, filter] as const,
  versionDetail: (rowId: number) => ["doseRuleVersionDetail", rowId] as const,
};

/** 목록 — keepPreviousData로 페이징·필터 변경 시 깜빡임 억제. */
export const useDoseReviewLogs = (query: DoseReviewLogListQuery, enabled: boolean) =>
  useQuery<DoseReviewLogListData>({
    queryKey: doseReviewLogKeys.list(query),
    queryFn: async () => unwrap(await doseReviewLogsApi.list(query)),
    enabled,
    keepPreviousData: true,
    staleTime: 0,
  });

/** 요약 — 목록과 독립(부분 실패 격리). */
export const useDoseReviewLogSummary = (filter: DoseReviewLogFilter, enabled: boolean) =>
  useQuery<DoseReviewLogSummaryData>({
    queryKey: doseReviewLogKeys.summary(filter),
    queryFn: async () => unwrap(await doseReviewLogsApi.summary(filter)),
    enabled,
    staleTime: 0,
  });

/** 상세 — range 없으면 직접 진입(from·to 없이 요청). logId 없으면 비활성. */
export const useDoseReviewLogDetail = (logId: number | undefined, range?: ApiRange) =>
  useQuery<DoseReviewLogDetailData>({
    queryKey: doseReviewLogKeys.detail(logId as number, range),
    queryFn: async () => unwrap(await doseReviewLogsApi.detail(logId as number, range)),
    enabled: logId != null,
    staleTime: 0,
  });

/** 이전/다음·순번 — 기간이 있을 때만 enabled(from·to 필수). */
export const useDoseReviewLogNeighbors = (
  logId: number | undefined,
  filter: DoseReviewLogFilter,
  enabled: boolean
) =>
  useQuery<DoseReviewLogNeighbors>({
    queryKey: doseReviewLogKeys.neighbors(logId as number, filter),
    queryFn: async () => unwrap(await doseReviewLogsApi.neighbors(logId as number, filter)),
    enabled,
    staleTime: 0,
  });

/**
 * 당시 룰 본문 — 버전 행마다 쿼리 1개(결정 E1). 한 룰의 실패가 그 룰만 폴백되게 한다.
 * 같은 rowId는 캐시 공유. rowIds는 rowIdsToFetch로 중앙 룰만·중복 제거된 것.
 */
export const useDoseRuleVersionDetails = (rowIds: number[]) =>
  useQueries(
    rowIds.map((rowId) => ({
      queryKey: doseReviewLogKeys.versionDetail(rowId),
      queryFn: async (): Promise<AdminDoseRuleVersionDetail> => {
        const res = await doseRuleAdminApi.versionDetail(rowId);
        if (res.error !== 0 || !res.data) {
          throw new DoseApiError({ code: res.error, message: res.message, data: res.data });
        }
        return res.data;
      },
      staleTime: 0,
    }))
  );
